import { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { ChevronRight, Clock, FileText, Folder, Link2, LogOut, MessageSquare, Moon, PanelLeftClose, Plus, Search, Settings, Sun, Laptop, UserRound } from "lucide-react";
import { useCreateSpace, useLogout, useMe, useSpaceTree, useSpaces } from "@/api/hooks";
import type { NoteTreeNode, Space } from "@/api/types";
import { cn, isMac } from "@/lib/utils";
import { useTheme } from "@/lib/theme";
import { Mark } from "@/components/Mark";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/lib/toast";
import { errorMessage } from "@/api/client";

const navItem = ({ isActive }: { isActive: boolean }) =>
  cn(
    "flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-colors",
    isActive ? "bg-foreground/[0.07] font-medium text-foreground" : "text-sidebar-foreground/80 hover:bg-foreground/[0.05] hover:text-foreground",
  );

export function Sidebar({
  onNavigate,
  onOpenPalette,
  onCollapse,
}: {
  onNavigate?: () => void;
  onOpenPalette: () => void;
  /** Desktop only: hide the sidebar (the mobile drawer has its own close button). */
  onCollapse?: () => void;
}) {
  const { data: spaces } = useSpaces();
  const { data: me } = useMe();
  const logout = useLogout();
  const theme = useTheme();
  const [newSpace, setNewSpace] = useState(false);

  const ThemeIcon = theme.mode === "system" ? Laptop : theme.mode === "dark" ? Moon : Sun;

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between px-3 pb-2 pt-4">
        <NavLink to="/" onClick={onNavigate} className="flex items-center gap-2 px-2 text-base font-semibold tracking-tight">
          <Mark className="size-6 shrink-0" />
          Minne
        </NavLink>
        {onCollapse && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon-xs" aria-label="Hide sidebar" onClick={onCollapse}>
                <PanelLeftClose />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Hide sidebar ({isMac() ? "⌘" : "Ctrl"}B)</TooltipContent>
          </Tooltip>
        )}
      </div>

      <div className="px-3">
        <button
          type="button"
          onClick={onOpenPalette}
          className="flex w-full items-center gap-2 rounded-md border bg-card px-2.5 py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <Search className="size-4" />
          <span className="flex-1 text-left">Search…</span>
          <kbd className="hidden rounded border bg-muted px-1.5 font-mono text-[10px] text-muted-foreground md:inline">{isMac() ? "⌘" : "Ctrl"} K</kbd>
        </button>
      </div>

      <nav className="mt-3 flex flex-col gap-0.5 px-3" aria-label="Main">
        <NavLink to="/" end className={navItem} onClick={onNavigate}>
          <Clock className="size-4" /> Recent
        </NavLink>
        <NavLink to="/threads" className={navItem} onClick={onNavigate}>
          <MessageSquare className="size-4" /> Threads
        </NavLink>
        <NavLink to="/search" className={navItem} onClick={onNavigate}>
          <Search className="size-4" /> Search
        </NavLink>
      </nav>

      <div className="mt-5 flex items-center justify-between px-5">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Spaces</span>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon-xs" aria-label="New space" onClick={() => setNewSpace(true)}>
              <Plus />
            </Button>
          </TooltipTrigger>
          <TooltipContent>New space</TooltipContent>
        </Tooltip>
      </div>
      <div className="mt-1 flex-1 overflow-y-auto px-3 pb-3">
        <ul className="flex flex-col gap-0.5">
          {(spaces ?? []).map((s) => (
            <SpaceItem key={s.id} space={s} onNavigate={onNavigate} />
          ))}
          {spaces && spaces.length === 0 && <li className="px-2.5 py-1.5 text-sm text-muted-foreground">No spaces yet</li>}
        </ul>
      </div>

      <div className="border-t px-3 py-2">
        <nav className="flex flex-col gap-0.5" aria-label="Settings">
          <NavLink to="/settings/account" className={navItem} onClick={onNavigate}>
            <UserRound className="size-4" /> Account
          </NavLink>
          <NavLink to="/settings/tokens" className={navItem} onClick={onNavigate}>
            <Settings className="size-4" /> API tokens
          </NavLink>
          <NavLink to="/settings/connect" className={navItem} onClick={onNavigate}>
            <Link2 className="size-4" /> Connect a client
          </NavLink>
        </nav>
        <div className="mt-2 flex items-center justify-between gap-2 px-1 pt-1">
          <div className="min-w-0 truncate text-xs text-muted-foreground" title={me?.username}>
            {me?.displayName || me?.username}
          </div>
          <div className="flex items-center">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon-xs" onClick={theme.cycle} aria-label={`Theme: ${theme.mode}`}>
                  <ThemeIcon />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Theme: {theme.mode}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon-xs" onClick={() => logout.mutate()} aria-label="Sign out">
                  <LogOut />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Sign out</TooltipContent>
            </Tooltip>
          </div>
        </div>
      </div>

      <NewSpaceDialog open={newSpace} onOpenChange={setNewSpace} />
    </div>
  );
}

function SpaceItem({ space, onNavigate }: { space: Space; onNavigate?: () => void }) {
  const location = useLocation();
  const active = location.pathname === `/spaces/${space.id}`;
  const [open, setOpen] = useState(active);
  const tree = useSpaceTree(space.id, open);

  return (
    <li>
      <div className={cn("group flex items-center rounded-md", active ? "bg-foreground/[0.07]" : "hover:bg-foreground/[0.05]")}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-label={open ? "Collapse" : "Expand"}
          className="flex size-7 shrink-0 items-center justify-center text-muted-foreground hover:text-foreground"
        >
          <ChevronRight className={cn("size-3.5 transition-transform", open && "rotate-90")} />
        </button>
        <NavLink
          to={`/spaces/${space.id}`}
          onClick={onNavigate}
          className={cn("flex min-w-0 flex-1 items-center gap-2 py-1.5 pr-2 text-sm", active ? "font-medium text-foreground" : "text-sidebar-foreground/80 hover:text-foreground")}
        >
          <Folder className="size-4 shrink-0" />
          <span className="truncate">{space.name}</span>
        </NavLink>
      </div>
      {open && (
        <ul className="ml-[18px] mt-0.5 border-l pl-1">
          {tree.isLoading && <li className="px-2 py-1 text-xs text-muted-foreground">Loading…</li>}
          {tree.data?.map((n) => (
            <TreeItem key={n.id} node={n} depth={0} onNavigate={onNavigate} />
          ))}
          {tree.data && tree.data.length === 0 && <li className="px-2 py-1 text-xs text-muted-foreground">No pages</li>}
        </ul>
      )}
    </li>
  );
}

function TreeItem({ node, depth, onNavigate }: { node: NoteTreeNode; depth: number; onNavigate?: () => void }) {
  const location = useLocation();
  const active = location.pathname === `/notes/${node.id}` || location.pathname === `/notes/${node.id}/edit`;
  const hasChildren = node.children && node.children.length > 0;
  const [open, setOpen] = useState(false);
  return (
    <li>
      <div className={cn("flex items-center rounded-md", active ? "bg-foreground/[0.07]" : "hover:bg-foreground/[0.05]")} style={{ paddingLeft: depth * 10 }}>
        {hasChildren ? (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-label={open ? "Collapse" : "Expand"}
            className="flex size-6 shrink-0 items-center justify-center text-muted-foreground hover:text-foreground"
          >
            <ChevronRight className={cn("size-3 transition-transform", open && "rotate-90")} />
          </button>
        ) : (
          <span className="size-6 shrink-0" />
        )}
        <NavLink
          to={`/notes/${node.id}`}
          onClick={onNavigate}
          className={cn("flex min-w-0 flex-1 items-center gap-1.5 py-1 pr-2 text-[13px]", active ? "font-medium text-foreground" : "text-sidebar-foreground/75 hover:text-foreground")}
        >
          <FileText className="size-3.5 shrink-0 opacity-70" />
          <span className="truncate">{node.title || "Untitled"}</span>
        </NavLink>
      </div>
      {open && hasChildren && (
        <ul>
          {node.children.map((c) => (
            <TreeItem key={c.id} node={c} depth={depth + 1} onNavigate={onNavigate} />
          ))}
        </ul>
      )}
    </li>
  );
}

function NewSpaceDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const [name, setName] = useState("");
  const create = useCreateSpace();
  const navigate = useNavigate();
  const { toast } = useToast();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          className="contents"
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim()) return;
            create.mutate(name.trim(), {
              onSuccess: (s) => {
                setName("");
                onOpenChange(false);
                navigate(`/spaces/${s.id}`);
              },
              onError: (err) => toast({ title: "Could not create space", description: errorMessage(err), tone: "error" }),
            });
          }}
        >
          <DialogHeader>
            <DialogTitle>New space</DialogTitle>
          </DialogHeader>
          <div className="grid gap-2">
            <Label htmlFor="space-name">Name</Label>
            <Input id="space-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Projects" autoFocus />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || create.isPending}>
              {create.isPending ? "Creating…" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
